import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import Switch, { SwitchClickHandler } from '@splunk/react-ui/Switch';
import { ThemeProvider, useLegacyThemeServiceKey } from '../../../../common/theme/ThemeProvider';
import P from '@splunk/react-ui/Paragraph';
import { EntityTypeSelect } from '@splunk/olly-imm/build/entity/EntityTypeSelect/EntityTypeSelect';
import { isEqual } from 'lodash';
import { useEntityTypes } from '@splunk/olly-imm/build/entity/hooks/useEntityTypes';
import { variables } from '@splunk/themes';
import {
    getFirstSuggestedEntityTypeIdentifier,
    isRecommendedEntityTypeSelectionValid,
} from '@splunk/olly-imm/build/entity/utils/entityTypeUtils';

interface EntityTypeSummary {
    contributing: boolean;
    types: string[];
}

interface EntityHealthContentProps {
    entityTypeSummary?: EntityTypeSummary;
    isNewDetector: boolean;
    onChange: (entityTypeSummary: EntityTypeSummary) => void;
    onValidationChange?: (isValid: boolean) => void;
    dimensionsMap?: Map<string, Set<string>>;
}

const Container = styled.div`
    padding: 8px;
`;

const EntityContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: start;
    gap: ${variables.spacingSmall};
`;

const EntityHealthContent: React.FC<EntityHealthContentProps> = ({
    entityTypeSummary,
    isNewDetector,
    onChange,
    onValidationChange,
    dimensionsMap,
}): JSX.Element => {
    const initialEntityType = entityTypeSummary?.types[0];
    const initialContributing = entityTypeSummary?.contributing ?? isNewDetector;
    const [currentEntityType, setCurrentEntityType] = useState(initialEntityType);
    const [attachToEntityHealth, setAttachToEntityHealth] = useState<boolean>(initialContributing);

    const themeKey = useLegacyThemeServiceKey();

    useEffect(() => {
        setCurrentEntityType(entityTypeSummary?.types[0]);
        setAttachToEntityHealth(entityTypeSummary?.contributing ?? isNewDetector);
    }, [entityTypeSummary, isNewDetector]);

    const dimensions = React.useMemo(() => {
        if (!dimensionsMap || dimensionsMap.size === 0) return [];
        const allDims = new Set<string>();
        dimensionsMap.forEach((dims) => {
            dims.forEach((dim) => allDims.add(dim));
        });
        return Array.from(allDims);
    }, [dimensionsMap]);

    const metrics = React.useMemo(() => {
        if (!dimensionsMap || dimensionsMap.size === 0) return [];
        return Array.from(dimensionsMap.keys());
    }, [dimensionsMap]);
    const hasEntityTypeContext = dimensions.length > 0 || metrics.length > 0;
    const hasHandledInitialNewDetectorSuggestionRef = useRef(false);
    const hasResolvedDimensionsMapRef = useRef(dimensionsMap !== undefined);
    const previousDimensionsRef = useRef(dimensions);
    const previousMetricsRef = useRef(metrics);

    const { entityTypes, isLoading } = useEntityTypes(
        hasEntityTypeContext ? { dimensions, metrics } : undefined
    );
    const isEntityTypeSelectionValid =
        !attachToEntityHealth ||
        isLoading ||
        isRecommendedEntityTypeSelectionValid(entityTypes, currentEntityType);

    const onEntityChange = (newValue: string | undefined): void => {
        setCurrentEntityType(newValue);
        onChange({
            contributing: attachToEntityHealth,
            types: newValue ? [newValue] : [],
        });
    };

    useEffect(() => {
        onValidationChange?.(isEntityTypeSelectionValid);
    }, [isEntityTypeSelectionValid, onValidationChange]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        const dimensionsChanged = !isEqual(previousDimensionsRef.current, dimensions);
        const metricsChanged = !isEqual(previousMetricsRef.current, metrics);
        const entityTypeContextChanged = dimensionsChanged || metricsChanged;
        const isFirstResolvedDimensionsMap =
            !hasResolvedDimensionsMapRef.current && dimensionsMap !== undefined;
        const shouldApplyInitialSuggestion =
            isNewDetector &&
            hasEntityTypeContext &&
            !hasHandledInitialNewDetectorSuggestionRef.current;
        const shouldPreserveExistingEntityTypeOnFirstContextLoad =
            !isNewDetector && hasEntityTypeContext && isFirstResolvedDimensionsMap;

        if (
            !shouldApplyInitialSuggestion &&
            !entityTypeContextChanged &&
            !isFirstResolvedDimensionsMap
        ) {
            return;
        }

        if (shouldApplyInitialSuggestion && attachToEntityHealth) {
            const suggestedEntityType = getFirstSuggestedEntityTypeIdentifier(entityTypes);

            if (suggestedEntityType !== currentEntityType) {
                setCurrentEntityType(suggestedEntityType);
                onChange({
                    contributing: true,
                    types: suggestedEntityType ? [suggestedEntityType] : [],
                });
            }
        } else if (
            isFirstResolvedDimensionsMap &&
            attachToEntityHealth &&
            hasEntityTypeContext &&
            currentEntityType === undefined
        ) {
            const suggestedEntityType = getFirstSuggestedEntityTypeIdentifier(entityTypes);

            if (suggestedEntityType !== currentEntityType) {
                setCurrentEntityType(suggestedEntityType);
                onChange({
                    contributing: true,
                    types: suggestedEntityType ? [suggestedEntityType] : [],
                });
            }
        } else if (
            entityTypeContextChanged &&
            attachToEntityHealth &&
            !shouldPreserveExistingEntityTypeOnFirstContextLoad
        ) {
            const suggestedEntityType = getFirstSuggestedEntityTypeIdentifier(entityTypes);

            if (suggestedEntityType !== currentEntityType) {
                setCurrentEntityType(suggestedEntityType);
                onChange({
                    contributing: true,
                    types: suggestedEntityType ? [suggestedEntityType] : [],
                });
            }
        }

        hasHandledInitialNewDetectorSuggestionRef.current =
            hasHandledInitialNewDetectorSuggestionRef.current || shouldApplyInitialSuggestion;
        hasResolvedDimensionsMapRef.current =
            hasResolvedDimensionsMapRef.current || dimensionsMap !== undefined;
        previousDimensionsRef.current = dimensions;
        previousMetricsRef.current = metrics;
    }, [
        attachToEntityHealth,
        currentEntityType,
        dimensions,
        dimensionsMap,
        entityTypes,
        hasEntityTypeContext,
        isLoading,
        isNewDetector,
        metrics,
        onChange,
    ]);

    const handleToggleValue: SwitchClickHandler = (): void => {
        if (attachToEntityHealth) {
            setAttachToEntityHealth(false);
            setCurrentEntityType(undefined);
            onChange({
                contributing: false,
                types: [],
            });
        } else {
            const suggestedEntityType =
                currentEntityType || getFirstSuggestedEntityTypeIdentifier(entityTypes);
            setAttachToEntityHealth(true);
            setCurrentEntityType(suggestedEntityType);
            onChange({
                contributing: true,
                types: suggestedEntityType ? [suggestedEntityType] : [],
            });
        }
    };

    return (
        <ThemeProvider colorScheme={themeKey}>
            <Container>
                <P>
                    The signals selected will influence the type of entity suggestions offered
                    below. Attaching a detector to an entity&#39;s health means it will affect the
                    health of a targeted entity, such as a service.
                </P>

                <EntityContainer>
                    <Switch
                        selected={attachToEntityHealth}
                        onClick={handleToggleValue}
                        appearance="toggle"
                    >
                        Attach detector to entity health
                    </Switch>

                    {attachToEntityHealth && (
                        <EntityTypeSelect
                            onChange={onEntityChange}
                            value={currentEntityType}
                            entityTypes={entityTypes}
                            mode="recommended"
                            hasError={!isEntityTypeSelectionValid}
                        />
                    )}
                </EntityContainer>
            </Container>
        </ThemeProvider>
    );
};

export default EntityHealthContent;
